/**
 * Tuning constants shared by the client and the relay server.
 *
 * Physics constants live in `src/client/physics/pogo.ts` — this file is only for
 * values both sides of the wire have to agree on, plus the level seed.
 */

/**
 * Seed for `buildLevel`. Every client must build the identical course.
 *
 * Not an arbitrary number. `npx tsx src/shared/level.sweep.ts 1 600` builds
 * every seed in the range, runs the whole of the level self-test on each, and
 * scores the survivors out of a hundred on how much of the generator the
 * mountain they describe actually shows a player: whether every zone walked the
 * radial band its table gives it and whether those bands came out looking
 * different from each other, which of the seven moving things it managed to
 * place, how many zones carry three of them, how many motifs and promises its
 * forks cover, and how little of it stands on its own head. A hundred and eighty
 * of the six hundred pass. 418 scores 98.0, the best of them, and what that
 * number is made of is the point:
 *
 * - **A silhouette, and not a table describing one.** The trunk's radius by
 *   zone, against the band each zone declares:
 *
 *   | zone | band | built | mean |
 *   |------|------|-------|------|
 *   | Meadow Foothills | 18–30 | 16.8–28.6 | 23.7 |
 *   | Verdant Steps    | 15–27 | 15.1–26.6 | 22.1 |
 *   | Sunken Ruins     | 11–21 | 11.0–17.5 | 13.7 |
 *   | Crystal Hollow   | 10–26 | 10.0–21.3 | 13.4 |
 *   | Frozen Shelf     | 14–28 | 13.5–27.2 | 20.5 |
 *   | Obsidian Crown   | 10–14 | 12.0–16.0 | 13.3 |
 *
 *   Four of the six reach past nine tenths of the way to their own rim and the
 *   flattest, the Ruins, still gets two thirds of the way — and ten and a half
 *   metres separate the widest zone mean from the tightest. That is a broad
 *   sunlit foot, a skirt on top of it, a pinch, a terraced middle, arms out of a
 *   narrow core, a broad cornice and a tight black spire, in that order, visible
 *   from the plaza. (The two figures that fall outside their own band are the
 *   blends doing their job: the Meadow's 16.8 is the waist at the y = 30
 *   checkpoint, and the Crown's 16.0 is the summit flare opening back out to
 *   r = 12 through it.)
 * - **137 landings on 512 primitives**, a little over the middle of the range —
 *   which is where a climb has room in it without the broad phase paying for
 *   the room.
 * - **Seven forks, five of the six motifs, all three promises.** A bold traverse
 *   and a scenic island spur off the Verdant skirt, a safe ledge wall and a
 *   second bold traverse through the Sunken Ruins, a long safe bridge down the
 *   Crystal Hollow, and a bold flight of mushrooms and one more bridge across
 *   the Frozen Shelf. Neither of the two teaching zones forks and neither does
 *   the crown: the first thirty metres are one path with one idea in it, and the
 *   last forty are one line and no choices, which is the right way for this
 *   mountain to open and to end.
 * - **Everything that can move, moves somewhere.** Eleven sliding pads spread
 *   over every zone above the tutorial, a pair of blinking pads and a lift in
 *   the Hollow, a carousel on the Shelf and a ferry on the way onto the summit —
 *   sixteen moving landings — plus four sweeper bars and three swinging logs,
 *   with 3/5/6/6/3 moving or hazardous primitives in the five zones above the
 *   tutorial and nothing whatever in the Meadow, which is what being the
 *   tutorial means here. The summit approach is clean: nothing hazardous stands
 *   on the last six trunk nodes, and the highest bar turns twenty-two metres
 *   below the last landing.
 * - **No stacked pairs at all**, against a self-test budget of eight and a
 *   generator median of six. You can look up anywhere on this climb and see sky.
 *
 * What it does not build is a colonnade, and the trade was not close. The best
 * seed that covers a sixth motif is 282 at 96.5, and it pays for the extra shape
 * with four stacked pairs — the ceiling this whole geometry exists to get rid
 * of, sold back four times over. A shape you have not met before is worth
 * something; it is not worth being able to see the underside of the next lap.
 *
 * Changing this rebuilds the whole mountain and (deliberately) invalidates every
 * saved checkpoint.
 */
export const LEVEL_SEED = 418;

// ---------------------------------------------------------------------------
// Networking rates
// ---------------------------------------------------------------------------

/** How often a client may send its state upstream (only on a meaningful delta). */
export const CLIENT_SEND_HZ = 12;

/** How often the server broadcasts a snapshot (skipped when nothing changed). */
export const SERVER_TICK_HZ = 15;

/** Ghosts are rendered this far in the past so interpolation always has two samples. */
export const INTERP_DELAY_MS = 130;

/** Past the interpolation window, extrapolate at most this long, then freeze the ghost. */
export const EXTRAPOLATE_MAX_MS = 250;

/** How much snapshot history a ghost keeps before old samples are dropped. */
export const SNAPSHOT_BUFFER_MS = 1000;

// ---------------------------------------------------------------------------
// Room limits
// ---------------------------------------------------------------------------

/** Hard cap on concurrent players in one room. */
export const MAX_PLAYERS = 64;

/** Maximum player name length after sanitisation. */
export const MAX_NAME_LEN = 16;

/** Server sends a `ping` this often. */
export const PING_INTERVAL_MS = 15_000;

/** A socket silent for this long is terminated. */
export const TIMEOUT_MS = 30_000;

/** Client reconnect delays in ms; the last value repeats. ±20% jitter is applied. */
export const RECONNECT_BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000];

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

/**
 * 16 player hues, evenly spaced every 22.5° around the wheel at HSL(h, 70%, 60%).
 * Precomputed so the client, the server and the start-screen swatches can never
 * disagree, and so nothing has to run a colour conversion at boot.
 *
 * Index 0 is red; the list walks the wheel clockwise through orange, yellow,
 * green, cyan, blue, violet and back to pink.
 */
export const PALETTE: readonly number[] = [
  0xe05252, // h =   0.0°
  0xe08752, // h =  22.5°
  0xe0bd52, // h =  45.0°
  0xcfe052, // h =  67.5°
  0x99e052, // h =  90.0°
  0x63e052, // h = 112.5°
  0x52e075, // h = 135.0°
  0x52e0ab, // h = 157.5°
  0x52e0e0, // h = 180.0°
  0x52abe0, // h = 202.5°
  0x5275e0, // h = 225.0°
  0x6352e0, // h = 247.5°
  0x9952e0, // h = 270.0°
  0xcf52e0, // h = 292.5°
  0xe052bd, // h = 315.0°
  0xe05287, // h = 337.5°
];

/** Base bone colour, before it is lerped toward a player's hue. */
export const BONE_COLOR = 0xede6d2;
